import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { CheckSquare } from "lucide-react";
import { TaskItem } from "@/components/TaskItem";

const GROUPS = [
  { key: "overdue", label: "Overdue", tone: "text-red-600" },
  { key: "due_soon", label: "Due within 7 days", tone: "text-amber-600" },
  { key: "later", label: "Later", tone: "text-slate-700" },
  { key: "no_due", label: "No due date", tone: "text-slate-500" },
];

export default function MyTasksPage() {
  const [data, setData] = useState(null);
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    api.get("/tasks/mine", { params: { include_completed: showDone } }).then((r) => setData(r.data));
  }, [showDone]);

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8" data-testid="my-tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline">Personal</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">My Tasks</h1>
          <p className="text-sm text-slate-500 mt-1">Every open action assigned to you across active projects. Click to jump straight to the action.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={showDone} onCheckedChange={setShowDone} data-testid="my-tasks-show-completed-switch" />Show recently completed</label>
      </div>

      {!data ? <p className="mt-8 text-slate-500">Loading…</p> : data.counts.total === 0 && !showDone ? (
        <div className="mt-8 rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500" data-testid="my-tasks-empty">
          <CheckSquare className="h-6 w-6 mx-auto text-slate-400" /><p className="mt-3 text-sm">No open actions assigned to you.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          <div className="flex flex-wrap gap-4 text-sm" data-testid="my-tasks-counts">
            <span><strong className="font-mono">{data.counts.total}</strong> open</span>
            <span className="text-red-600"><strong className="font-mono">{data.counts.overdue}</strong> overdue</span>
            <span className="text-amber-600"><strong className="font-mono">{data.counts.due_soon}</strong> due soon</span>
          </div>
          {GROUPS.filter((g) => data.groups[g.key].length).map((g) => (
            <section key={g.key} data-testid={`my-tasks-group-${g.key}`}>
              <h2 className={`text-sm font-semibold uppercase tracking-wider mb-2 ${g.tone}`}>{g.label} <span className="font-mono text-slate-400">({data.groups[g.key].length})</span></h2>
              <div className="space-y-2">{data.groups[g.key].map((i) => <TaskItem key={i.action_id} item={i} testid={`my-task-${i.action_id}`} />)}</div>
            </section>
          ))}
          {showDone && (
            <section data-testid="my-tasks-recent-completed">
              <h2 className="text-sm font-semibold uppercase tracking-wider mb-2 text-emerald-700">Recently completed <span className="font-mono text-slate-400">({data.recent_completed.length})</span></h2>
              <div className="space-y-2 opacity-70">{data.recent_completed.map((i) => <TaskItem key={i.action_id} item={i} testid={`my-task-done-${i.action_id}`} />)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
